// ./src/utils/analytics/index.ts
import Analytics from './Analytics';
import { AppInsightsProvider } from './providers/AppInsightsProvider';
import { GoogleAnalyticsProvider } from './providers/GoogleAnalyticsProvider';
import { appEnv } from '@/config/branding';
import {
  PURCHASE_EVENT,
  PlansSource,
  PurchaseFacts,
  UPGRADE_FUNNEL_EVENT,
  UpgradeFunnelItem,
  purchaseParams,
  upgradeCheckoutParams,
  upgradeViewParams,
} from './purchase';

export * from './types';
export * from './consent';
export * from './privacy';
export * from './funnel';
export * from './pageView';
export * from './purchase';
export * from './subscriptionPurchase';
export { Analytics, GoogleAnalyticsProvider };

// Create and export singleton instance
const analytics = new Analytics({
  debug: import.meta.env.DEV,
});

// ⚠ A dev server sends NOTHING to Google unless asked (VITE_ANALYTICS_IN_DEV=true) — `.env` carries the production tag,
//   so every local click used to land in the real property's funnel. Asked for, it is marked `debug_mode` (GA's
//   DebugView). The console log above (`debug`) shows every event either way.
const sendFromDev = import.meta.env.VITE_ANALYTICS_IN_DEV === 'true';
const gaTrackingId = appEnv.googleTag;

/**
 * Is anything collecting on this build? ⚠ The consent banner and the profile switch show ONLY when it is — asking about
 * cookies that are never set would be untrue on screen.
 */
export const analyticsEnabled = !!gaTrackingId && (!import.meta.env.DEV || sendFromDev);

if (analyticsEnabled) {
  analytics.addProvider(new GoogleAnalyticsProvider(gaTrackingId, { debugMode: import.meta.env.DEV }));
}

if (appEnv.appInsightsConnectionString) {
  analytics.addProvider(new AppInsightsProvider(appEnv.appInsightsConnectionString, [new URL(appEnv.graphqlEndpoint, window.location.href).hostname]));
}

export { analytics };

// The transactions this tab has already reported (and, best-effort, this browser session's): a purchase is counted once.
const SENT_PURCHASES_KEY = 'em_purchases_sent';
const sentPurchases = new Set<string>();

const purchaseAlreadySent = (transactionId: string): boolean => {
  if (sentPurchases.has(transactionId)) {
    return true;
  }

  try {
    return (JSON.parse(window.sessionStorage.getItem(SENT_PURCHASES_KEY) || '[]') as string[]).includes(transactionId);
  } catch {
    return false;
  }
};

const rememberPurchase = (transactionId: string): void => {
  sentPurchases.add(transactionId);

  try {
    const list = JSON.parse(window.sessionStorage.getItem(SENT_PURCHASES_KEY) || '[]') as string[];

    window.sessionStorage.setItem(SENT_PURCHASES_KEY, JSON.stringify([...list.filter((id) => id !== transactionId), transactionId].slice(-50)));
  } catch {
    // Private mode / blocked storage: the in-memory set still covers this tab.
  }
};

/**
 * Report a completed purchase (`purchase.ts`) — once per transaction. Missing or malformed facts send nothing.
 * ⚠ Call it where the purchase is CONFIRMED (Stripe took the card, a code granted it, the plan is on the account), never
 *   when a payment merely starts.
 */
export const trackPurchase = (facts: PurchaseFacts): void => {
  const params = purchaseParams(facts);
  const transactionId = params ? String(params.transaction_id) : '';

  if (!params || purchaseAlreadySent(transactionId)) {
    return;
  }

  rememberPurchase(transactionId);
  analytics.sendEvent(PURCHASE_EVENT, params);
};

/** #60 — the plans this event was offered were SEEN (`view_item_list`). Call once per opening of the plans. */
export const trackUpgradeView = (items: UpgradeFunnelItem[], source: PlansSource): void => {
  const params = upgradeViewParams(items, source);

  if (params) {
    analytics.sendEvent(UPGRADE_FUNNEL_EVENT.VIEW, params);
  }
};

/** #60 — a plan was CHOSEN (`begin_checkout`), before the card — the step between seeing the plans and the purchase. */
export const trackUpgradeCheckout = (item: UpgradeFunnelItem, source: PlansSource): void => {
  const params = upgradeCheckoutParams(item, source);

  if (params) {
    analytics.sendEvent(UPGRADE_FUNNEL_EVENT.CHECKOUT, params);
  }
};
